import { useRevealOnScroll } from "@/hooks/use-reveal-on-scroll";

const stories = [
  {
    src: "/landing/prime-advisor-tablet.jpg",
    alt: "Mortgage advisor reviewing plans with a smiling customer on a tablet",
    title: "Closer conversations",
    body: "Sit with clients, not paperwork. Your assistant keeps the next steps ready while you talk.",
  },
  {
    src: "/landing/prime-happy-homeowners.jpg",
    alt: "Happy homeowners celebrating with house keys outside their new home",
    title: "Clients who feel cared for",
    body: "Warm follow-ups and clear updates turn anxious buyers into people who recommend you.",
  },
  {
    src: "/landing/prime-using-app.jpg",
    alt: "Mortgage professional using Mortgage Broker Assistant on a laptop",
    title: "Built into your day",
    body: "Chat, automated assistants, and connections live in one glass-clear workspace — on desk or on the go.",
  },
] as const;

function StoryCard({
  story,
  index,
}: {
  story: (typeof stories)[number];
  index: number;
}) {
  const ref = useRevealOnScroll<HTMLElement>();

  return (
    <article
      ref={ref}
      className={`landing-reveal landing-reveal-delay-${index + 1} group relative overflow-hidden rounded-3xl border border-white/70 bg-white/50 shadow-[0_28px_70px_-36px_rgba(15,23,42,0.45)] backdrop-blur-xl transition duration-500 hover:-translate-y-1.5 hover:shadow-[0_36px_80px_-32px_rgba(15,118,110,0.35)]`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={story.src}
          alt={story.alt}
          className="h-full w-full object-cover transition duration-700 ease-out group-hover:scale-[1.04]"
          loading="lazy"
          decoding="async"
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/55 via-slate-950/10 to-transparent"
          aria-hidden
        />
        <p className="absolute bottom-3 left-4 right-4 font-[family-name:var(--font-display)] text-lg font-semibold text-white drop-shadow">
          {story.title}
        </p>
      </div>
      <div className="px-5 py-4">
        <p className="text-sm leading-relaxed text-slate-600">{story.body}</p>
      </div>
    </article>
  );
}

export function LandingLifestyleSection() {
  const headerRef = useRevealOnScroll<HTMLElement>();

  return (
    <section
      id="life"
      className="mt-16 scroll-mt-24"
      aria-labelledby="life-heading"
    >
      <header ref={headerRef} className="landing-reveal mb-8 max-w-2xl">
        <p className="text-sm font-medium text-teal-800">In the real world</p>
        <h2
          id="life-heading"
          className="mt-2 font-[family-name:var(--font-display)] text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl"
        >
          Brokers, clients, and the moments that matter
        </h2>
        <p className="mt-3 text-slate-600">
          Mortgage Broker Assistant stays in the background so your time stays
          with people — consultations, closing days, and quiet hours catching up
          on the pipeline.
        </p>
      </header>
      <div className="grid gap-5 md:grid-cols-3">
        {stories.map((story, index) => (
          <StoryCard key={story.src} story={story} index={index} />
        ))}
      </div>
    </section>
  );
}
